import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { Notice } from '@verlo/shared/components/ui/notice';
import { allergenLabel, listWords } from '@verlo/shared/lib/allergens';
import { elsewhereWords, minutesSince } from '../../lib/kitchenBoard';
import { stationBoard, whereWords, atWords, lateness, lineAt, lineKey, needsAllergyCheck, UNDO_MS } from '../../lib/stationBoard';

// A station's own screen: option A from the station mocks (Martin, Oct 2026). One card per
// table, closest to the board the kitchen already knows; the whole dish row is the button.
// A quiet line under each dish says where it has come from and goes next, in words. A dish
// still at an earlier station is faded in the card it belongs to, so this station sees the
// table's share coming. Time is the only colour besides the allergy: copper after 10
// minutes, claret after 20. Navy is kept for nothing here: there's no button to find.

const timeClass = (m) => (lateness(m) === 'very-late' ? 'font-bold text-signal-stop' : lateness(m) === 'late' ? 'font-bold text-signal-warn' : 'text-ink-soft');

function Row({ line, kind, station, stations, onTap }) {
  const faded = kind === 'upstream';
  // Just ticked (M4): ticked, and for a few seconds still on its card, with Undo.
  const justDone = kind === 'undo';
  const ticked = kind === 'done' || justDone;
  return (
    <button
      type="button"
      onClick={onTap}
      disabled={faded || !onTap}
      className={`grid w-full grid-cols-[1fr_40px] items-center gap-x-3 gap-y-0.5 border-t border-hairline px-3 py-2.5 text-left ${
        faded ? 'cursor-default opacity-50' : onTap ? 'hover:bg-surface-2 active:bg-paper' : 'cursor-default'} ${ticked ? '' : 'min-h-14'}`}
      data-testid={`station-row-${line.line_id}`}
      data-kind={kind}
    >
      <span className={`text-[16px] leading-snug ${justDone ? 'text-ink-faint' : ticked ? 'text-ink-faint line-through' : 'text-ink'}`}>
        <b className="font-bold">{line.quantity}×</b> {line.name}
        {justDone && <span className="ml-2 border border-ink px-1.5 py-0.5 align-[2px] font-heading text-[11px] font-bold uppercase tracking-[0.05em] text-ink" data-testid="station-undo">Undo</span>}
      </span>
      <span
        className={`plan-round col-start-2 row-span-3 row-start-1 grid h-10 w-10 place-items-center rounded-full border-2 ${
          ticked ? 'border-ink bg-ink text-white' : faded ? 'border-dashed border-hairline-strong' : 'border-hairline-strong'}`}
        aria-hidden
      >
        {ticked && <Check size={18} strokeWidth={3} />}
      </span>
      {!ticked && line.modifiers?.length > 0 && (
        <span className="text-[13px] text-ink-soft">{line.modifiers.join(' · ')}</span>
      )}
      {!ticked && line.notes && <span className="text-[13px] italic text-ink-soft">Note: {line.notes}</span>}
      {!ticked && line.allergen_clash?.length > 0 && (
        <span className="text-[12px] font-bold uppercase tracking-[0.04em] text-signal-stop">Contains {listWords(line.allergen_clash)}</span>
      )}
      {/* What the dish lists that the till couldn't read, so couldn't check: the station
          cooking it is the one that has to (as on the kitchen's ticket card). */}
      {!ticked && line.allergen_unread?.length > 0 && (
        <span className="text-[12px] font-bold uppercase tracking-[0.04em] text-signal-stop" data-testid="station-check">Check: {line.allergen_unread.join(', ')}</span>
      )}
      {!ticked && line.allergy_notes && (
        <span className="border-l-[3px] border-signal-stop-line pl-2 text-[12.5px] font-bold text-signal-stop">{line.allergy_notes}</span>
      )}
      {!ticked && (
        <span className="text-[12px] text-ink-faint">
          {faded ? `still ${atWords(line, stations)}` : whereWords(line, station, stations)}
        </span>
      )}
    </button>
  );
}

function Card({ card, station, stations, now, onTick, small = false, done = false }) {
  const t = card.ticket;
  const m = minutesSince(t.fired_at, now);
  const allergy = t.has_allergies && [(t.allergens || []).map(allergenLabel).join(', '), t.allergy_notes].filter(Boolean).join(' · ');
  const elsewhere = elsewhereWords(card.elsewhere.map((l) => ({ ...l, station: lineAt(l) })), stations);
  const check = !done && needsAllergyCheck([...card.here, ...card.upstream]);
  // An allergy reads first, above every other message, as on the kitchen's ticket card.
  const messages = [...(t.messages || [])].sort((a, b) => (a.kind === 'allergy' ? 0 : 1) - (b.kind === 'allergy' ? 0 : 1));
  return (
    <div className={`mb-3 break-inside-avoid border bg-white ${check ? 'border-2 border-signal-stop-line' : 'border-hairline'} ${small && !done ? 'opacity-60' : ''}`} data-testid={`station-card-${t.id}`}>
      <div className="flex items-baseline gap-2 px-3 pb-1.5 pt-2.5">
        <b className={`font-heading font-extrabold text-ink ${small ? 'text-[17px]' : 'text-[20px]'}`}>{t.table_name || 'Bar'}</b>
        <span className="text-[13px] text-ink-faint">#{t.sequence}</span>
        {/* The table has an allergy, even when nobody has written down what. */}
        {t.has_allergies && <AlertTriangle size={15} className="shrink-0 self-center text-signal-warn" aria-label="Allergy at this table" data-testid="station-allergy-flag" />}
        <span className={`ml-auto text-[15px] tabular-nums ${timeClass(m)}`}>{m}m</span>
      </div>
      {allergy && (
        <div className="mx-3 mb-1.5 border-l-[3px] border-signal-stop-line py-0.5 pl-2 text-[12.5px] font-bold text-signal-stop" data-testid="station-allergy">
          {/* Led by the word, as on the Pass: a note that says "Nut allergy" read "… Nut allergy allergy". */}
          Allergy: {allergy}
        </div>
      )}
      {!done && messages.map((msg) => (
        <Notice key={msg.id} tone={msg.kind === 'allergy' ? 'stop' : 'info'} word={msg.kind === 'allergy' ? 'Allergy' : 'Table'} className="mx-3 mb-1.5 px-2.5 py-1 text-xs">
          {msg.text}
        </Notice>
      ))}
      {done
        ? card.doneHere.map((l) => <Row key={lineKey(l)} line={l} kind="done" station={station} stations={stations} onTap={onTick ? () => onTick(t, l, false) : undefined} />)
        : (
          // In the ticket's own order: a dish just ticked stays where it was, with Undo, rather
          // than dropping below the ones still to do.
          [...card.here.map((l) => [l, 'here']), ...(card.justDone || []).map((l) => [l, 'undo']), ...card.upstream.map((l) => [l, 'upstream'])]
            .sort((a, b) => (t.items || []).indexOf(a[0]) - (t.items || []).indexOf(b[0]))
            .map(([l, kind]) => (
              <Row key={lineKey(l)} line={l} kind={kind} station={station} stations={stations}
                onTap={kind === 'upstream' || !onTick ? undefined : () => onTick(t, l, kind === 'here')} />
            ))
        )}
      {(done || elsewhere) && (
        <div className="border-t border-dashed border-hairline px-3 py-1.5 text-[12px] text-ink-faint">
          {done ? 'Tap a dish to undo' : `+ ${elsewhere}`}
        </div>
      )}
    </div>
  );
}

// `onTick` left out: someone who can see the screen but not move tickets on (Oct 2026).
export default function StationBoard({ tickets, station, stations, now, onTick: tickOnServer }) {
  // Dishes ticked here in the last few seconds stay on their card with Undo (M4): a mis-tap
  // on a table with more to do used to vanish into Done, with no way back from the card.
  const [recent, setRecent] = useState(() => new Set());
  const timers = useRef({});
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);
  const forget = (key) => {
    clearTimeout(timers.current[key]);
    delete timers.current[key];
    setRecent((r) => { const n = new Set(r); n.delete(key); return n; });
  };
  const onTick = tickOnServer && ((t, l, done) => {
    const key = lineKey(l);
    if (done) {
      setRecent((r) => new Set(r).add(key));
      clearTimeout(timers.current[key]);
      timers.current[key] = setTimeout(() => forget(key), UNDO_MS);
    } else {
      forget(key);
    }
    tickOnServer(t, l, done);
  });
  const { todo, coming, done } = stationBoard(tickets, station, { now, recent });
  const routeIdx = (stations || []).findIndex((s) => s.key === station);
  const prevName = routeIdx > 0 ? stations[routeIdx - 1].name : null;
  const head = 'mb-2 flex justify-between font-heading text-[11px] font-bold uppercase tracking-[0.09em] text-ink-faint';
  return (
    <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_230px]" data-testid="station-board">
      <div className="min-w-0">
        <div className={head}><span>To do</span><b className="text-ink">{todo.reduce((n, c) => n + c.here.length, 0)}</b></div>
        {todo.length === 0 && (
          <div className="mb-3 border border-hairline bg-white px-4 py-6 text-center text-sm text-ink-soft" data-testid="station-clear">
            Nothing here now.{coming.length ? ' More is on its way.' : ''}
          </div>
        )}
        <div className="gap-3 sm:columns-2">
          {todo.map((c) => <Card key={c.ticket.id} card={c} station={station} stations={stations} now={now} onTick={onTick} />)}
        </div>
        {coming.length > 0 && (
          <>
            <div className={`${head} mt-2`}><span>Coming{prevName ? ` from ${prevName}` : ''}</span><b className="text-ink">{coming.length}</b></div>
            <div className="gap-3 sm:columns-2">
              {coming.map((c) => <Card key={c.ticket.id} card={c} station={station} stations={stations} now={now} onTick={onTick} small />)}
            </div>
          </>
        )}
      </div>
      <div className="min-w-0">
        <div className={head}><span>Done, last 10 min</span><b className="text-ink">{done.length}</b></div>
        {done.map((c) => <Card key={c.ticket.id} card={c} station={station} stations={stations} now={now} onTick={onTick} small done />)}
      </div>
    </div>
  );
}
